import { DecimalPipe } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { comparesWithCurrentState, isWholeOrganisation, modelsInScope, scopeForLinqset, filterForScope } from '../analysis/scope';
import { Change, changeFormat, changeFrom, measure } from '../analysis/comparison';
import { SKETCH_URL } from '../app-urls';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { ACTION_TYPE } from '../common/linqset-base';
import { CURRENT_STATE_PROJECT_KEY, Linqset, MODEL_TYPE } from '../common/linqset-model';
import { Currency, DEFAULT_CURRENCY, getCurrency } from '../common/organisation';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { InsightsService } from '../model-services/insights.service';
import { buildTagsAnalysis, measureOf, TAG_MEASURES, TaggedAction, TagGroupSummary, TagMeasure, TagsAnalysis, TagTotals, UNTAGGED_KEY } from './tags-analysis-model';

// the headline figures, as they're compared
const tagsFigures = (a: TagsAnalysis) => ({
    groups: measure(a.totals.groupsInUse, 'none', 'count'),
    actions: a.totals.actions ? measure(a.totals.tagged / a.totals.actions * 100, 'up', 'percent') : undefined,
    hours: a.totals.hours ? measure(a.totals.taggedHours / a.totals.hours * 100, 'up', 'percent') : undefined,
    cost: a.totals.cost ? measure(a.totals.taggedCost / a.totals.cost * 100, 'up', 'percent') : undefined,
    exception: a.totals.exceptionCost ? measure(a.totals.exceptionCost, 'down', 'money') : measure(a.totals.exceptionHours, 'down', 'hours'),
});

const HOW_DONE: Record<ACTION_TYPE, string> = {
    [ACTION_TYPE.NONE]: 'No one assigned',
    [ACTION_TYPE.AUTOMATED]: 'Automated',
    [ACTION_TYPE.MANUAL]: 'People',
    [ACTION_TYPE.BOTH]: 'People using systems',
    [ACTION_TYPE.EXTERNAL]: 'External',
};

// The work by tag: for each tag group, how much of the work in scope carries one of its tags, how the work splits
// between them, and the actions behind each tag. Groups on the left, the chosen group's tags in the middle, and the
// chosen tag's actions below, each a step closer to the model
@Component({
    selector: 'app-tags-analysis',
    standalone: true,
    imports: [DecimalPipe, MatButtonModule, MatButtonToggleModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
    template: `
        @if (loading) {
        <div class="status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Working out the tags…</span></div>
        }@else if (error) {
        <p class="status error" role="alert">{{ error }}</p>
        }@else if (analysis; as a) {
        <div class="tags" data-testid="tags-analysis">
            <p class="intro">Tags categorise actions within a tag group. Each group splits the work its way: pick a group to see how
                the work divides between its tags and how much of it carries no tag, then a tag to see the actions behind it.</p>

            @if (!a.groups.length) {
            <section class="panel empty" data-testid="tags-none">
                <mat-icon aria-hidden="true">sell</mat-icon>
                <div>
                    <strong>No tag groups yet</strong>
                    <p>Tag groups are set up for the organisation, under Node tag groups in the organisation's settings. Once a group has tags,
                        put them on actions in the model to see the work by tag here.</p>
                </div>
            </section>
            } @else {

            <!-- the headline figures -->
            <section class="tiles" aria-label="Headline figures">
                <div class="tile">
                    <span class="label"><mat-icon aria-hidden="true">sell</mat-icon>Tag groups in use</span>
                    <strong>{{ a.totals.groupsInUse }}<span class="of"> of {{ a.groups.length }}</span></strong>@if (vs('groups'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ tagCount }} {{ tagCount === 1 ? 'tag' : 'tags' }} in all</span>
                </div>
                <div class="tile" matTooltip="Actions with at least one tag, of all the actions looked at">
                    <span class="label"><mat-icon aria-hidden="true">label</mat-icon>Actions tagged</span>
                    <strong>{{ a.totals.actions ? (a.totals.tagged / a.totals.actions * 100 | number: '1.0-0') + '%' : '–' }}</strong>@if (vs('actions'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ a.totals.tagged | number }} of {{ a.totals.actions | number }} actions</span>
                </div>
                <div class="tile" matTooltip="Hours a year of the tagged actions, of all the hours looked at">
                    <span class="label"><mat-icon aria-hidden="true">schedule</mat-icon>Hours tagged</span>
                    <strong>{{ a.totals.hours ? (a.totals.taggedHours / a.totals.hours * 100 | number: '1.0-0') + '%' : '–' }}</strong>@if (vs('hours'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ hours(a.totals.taggedHours) }} of {{ hours(a.totals.hours) }} hours a year</span>
                </div>
                <div class="tile" matTooltip="Cost a year of the tagged actions, of all the cost looked at">
                    <span class="label"><mat-icon aria-hidden="true">payments</mat-icon>Cost tagged</span>
                    <strong>{{ a.totals.cost ? (a.totals.taggedCost / a.totals.cost * 100 | number: '1.0-0') + '%' : '–' }}</strong>@if (vs('cost'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ money(a.totals.taggedCost) }} of {{ money(a.totals.cost) }} a year</span>
                </div>
                <div class="tile" [class.warn]="a.totals.exceptionActions > 0" matTooltip="Actions that are exception work: on a detour from the main flow, or marked as such. Friction on the EPM scorecard, shown by tag below">
                    <span class="label"><mat-icon aria-hidden="true">alt_route</mat-icon>Exception work</span>
                    <strong>{{ a.totals.exceptionCost ? money(a.totals.exceptionCost) : a.totals.exceptionHours ? hours(a.totals.exceptionHours) + ' h' : '–' }}</strong>@if (vs('exception'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ a.totals.exceptionActions ? (a.totals.exceptionActions | number) + ' of ' + (a.totals.actions | number) + ' actions, ' + hours(a.totals.exceptionHours) + ' hours a year' : 'no work leaves the main flow' }}</span>
                </div>
            </section>

            <div class="body">
                <!-- the groups -->
                <nav class="panel groups" aria-label="Tag groups">
                    <h3>Tag groups</h3>
                    <p class="note">How much of the work each group's tags cover.</p>
                    <ul data-testid="tags-groups">
                        @for (g of a.groups; track g.key) {
                        <li>
                            <button type="button" class="group" [class.active]="g.key === groupKey" (click)="selectGroup(g)" [attr.aria-current]="g.key === groupKey ? 'true' : null" [attr.data-testid]="'tags-group-' + g.key">
                                <span class="group-head"><strong>{{ g.name }}</strong><span class="count">{{ g.tags.length }} {{ g.tags.length === 1 ? 'tag' : 'tags' }}</span></span>
                                <span class="coverage">
                                    <span class="track"><span class="fill" [style.width.%]="(g.coverage || 0) * 100"></span></span>
                                    <span class="value">{{ g.coverage === null ? '–' : (g.coverage * 100 | number: '1.0-0') + '%' }}</span>
                                </span>
                                <span class="meta">{{ g.tagged | number }} of {{ g.total | number }} actions tagged{{ g.appliesToAll ? '' : ' · some models' }}</span>
                            </button>
                        </li>
                        }
                    </ul>
                </nav>

                <!-- the chosen group -->
                @if (group; as g) {
                <section class="panel detail" [attr.aria-label]="g.name">
                    <div class="detail-head">
                        <div>
                            <h3>{{ g.name }}</h3>
                            <p class="note">{{ g.description || 'How the work splits between the group\\'s tags.' }}
                                @if (!g.appliesToAll) { This group is for some models only, so only their actions count.}</p>
                        </div>
                        <mat-button-toggle-group class="measure" [value]="measure" (change)="setMeasure($event.value)" aria-label="Measure" hideSingleSelectionIndicator>
                            @for (m of measures; track m.key) {<mat-button-toggle [value]="m.key">{{ m.label }}</mat-button-toggle>}
                        </mat-button-toggle-group>
                    </div>

                    @if (g.total) {
                    <!-- the split, as one bar -->
                    <div class="split" role="img" [attr.aria-label]="splitLabel(g)" data-testid="tags-split">
                        @for (t of segments(g); track t.key) {
                        <span class="segment" [class.untagged]="t.untagged" [class.selected]="t.key === tagKey" [style.flex-grow]="measureOf(t)" [style.background-color]="t.untagged ? null : t.colour"
                            [matTooltip]="t.name + ': ' + format(measureOf(t)) + ' ' + unit + ' (' + (shareOfAll(g, t) * 100 | number: '1.0-0') + '% of all)'" (click)="selectTag(g, t)"></span>
                        }
                    </div>

                    <!-- the tags -->
                    <div class="scroll">
                        <table class="list" data-testid="tags-table">
                            <thead><tr>
                                <th>Tag</th><th class="num">Actions</th><th class="num">Hours a year</th><th class="num">Cost a year</th><th class="num">kg CO2e</th><th class="num optional">Items a year</th>
                                <th class="num optional" matTooltip="Actions done by a system alone">Automated</th>
                                <th class="num" matTooltip="The cost (or hours) a year of the tag's actions that are exception work: on a detour from the main flow, or marked as such. Friction on the EPM scorecard">Exception work</th>
                            </tr></thead>
                            <tbody>
                                @for (t of rows(g); track t.key) {
                                <tr [class.untagged]="t.untagged" [class.selected]="t.key === tagKey" [class.quiet]="!t.actions" (click)="selectTag(g, t)" [attr.data-testid]="'tags-row-' + (t.untagged ? 'untagged' : t.key)">
                                    <td class="first">
                                        <span class="tag-name"><span class="swatch" [class.untagged]="t.untagged" [style.background-color]="t.untagged ? null : t.colour"></span>{{ t.name }}
                                            @if (t.untagged) {<span class="tag-note">none of the group's tags</span>}</span>
                                        @if (t.untagged) {
                                        <span class="share" matTooltip="Of all the group's work"><span class="mini"><span class="fill untagged" [style.width.%]="shareOfAll(g, t) * 100"></span></span>
                                            <span class="mini-value">{{ shareOfAll(g, t) * 100 | number: '1.0-0' }}% of all {{ measureLabel.toLowerCase() }}</span></span>
                                        } @else {
                                        <span class="share" [matTooltip]="'Share of the tagged ' + measureLabel.toLowerCase()"><span class="mini"><span class="fill" [style.background-color]="t.colour" [style.width.%]="share(g, t) * 100"></span></span>
                                            <span class="mini-value">{{ share(g, t) * 100 | number: '1.0-0' }}%</span></span>
                                        }
                                    </td>
                                    <td class="num">{{ t.actions | number }}</td>
                                    <td class="num">{{ t.actions ? hours(t.hours) : '–' }}@if (tagChange(g, t, 'hours'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}</td>
                                    <td class="num">{{ t.actions ? money(t.cost) : '–' }}@if (tagChange(g, t, 'cost'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}</td>
                                    <td class="num">{{ t.actions ? count(t.carbon) : '–' }}</td>
                                    <td class="num optional">{{ t.actions ? count(t.items) : '–' }}</td>
                                    <td class="num optional">{{ t.actions ? (t.automated / t.actions * 100 | number: '1.0-0') + '%' : '–' }}</td>
                                    <td class="num" [class.exception]="t.exceptionActions > 0">{{ exceptionOf(t) }}</td>
                                </tr>
                                }
                            </tbody>
                            <tfoot><tr>
                                <td class="first">All {{ g.total | number }} actions</td>
                                <td class="num">{{ g.total | number }}</td>
                                <td class="num">{{ hours(g.hours + g.untagged.hours) }}</td>
                                <td class="num">{{ money(g.cost + g.untagged.cost) }}</td>
                                <td class="num">{{ count(g.carbon + g.untagged.carbon) }}</td>
                                <td class="num optional">{{ count(g.items + g.untagged.items) }}</td>
                                <td class="num optional"></td>
                            </tr></tfoot>
                        </table>
                    </div>
                    } @else {
                    <p class="none">None of the actions looked at are in a model this group is for.</p>
                    }

                    <!-- the chosen tag's actions -->
                    @if (tag; as t) {
                    <section class="drill" [attr.aria-label]="'Actions tagged ' + t.name" data-testid="tags-drill">
                        <div class="drill-head">
                            <h4><span class="swatch" [class.untagged]="t.untagged" [style.background-color]="t.untagged ? null : t.colour"></span>
                                {{ t.untagged ? 'Actions with none of the group\\'s tags' : 'Actions tagged ' + t.name }}
                                <span class="count">{{ t.actions | number }}</span></h4>
                            <button mat-button (click)="selectTag(g, undefined)"><mat-icon>close</mat-icon>Close</button>
                        </div>
                        @if (t.description && !t.untagged) {<p class="note">{{ t.description }}</p>}
                        @if (t.models.length > 1) {
                        <div class="models" role="group" aria-label="Models">
                            <button type="button" class="chip" [class.active]="!modelFilter" (click)="modelFilter = ''">All models</button>
                            @for (m of t.models; track m) {<button type="button" class="chip" [class.active]="modelFilter === m" (click)="modelFilter = modelFilter === m ? '' : m">{{ m }}</button>}
                        </div>
                        }
                        <div class="scroll">
                            <table class="list actions" data-testid="tags-actions">
                                <thead><tr><th>Action</th><th>Model</th><th>How it's done</th><th>Roles</th><th class="num">Hours a year</th><th class="num">Cost a year</th><th class="num">kg CO2e</th><th class="num">Items a year</th>
                                    @if (otherGroups(g).length) {<th>Other tags</th>}</tr></thead>
                                <tbody>
                                    @for (x of actionsOf(t); track x.key) {
                                    <tr>
                                        <td class="first"><button type="button" class="link" (click)="openAction(x)" matTooltip="Open it in its model">{{ x.name }}<mat-icon aria-hidden="true">open_in_new</mat-icon></button>
                                            @if (x.exceptionWork) {<span class="exception-chip" matTooltip="Exception work: on a detour from the main flow, or marked as such. Friction on the EPM scorecard">Exception</span>}</td>
                                        <td class="muted model">{{ x.model }}</td>
                                        <td><span [class]="'how how-' + x.actionType">{{ howDone(x.actionType) }}</span></td>
                                        <td class="roles">{{ x.roles.join(', ') || '–' }}</td>
                                        <td class="num">{{ hours(x.hours) }}</td>
                                        <td class="num">{{ x.cost ? money(x.cost) : '–' }}</td>
                                        <td class="num">{{ x.carbon ? count(x.carbon) : '–' }}</td>
                                        <td class="num">{{ x.items ? count(x.items) : '–' }}</td>
                                        @if (otherGroups(g).length) {
                                        <td class="other">@for (o of otherTags(x, g); track o.key) {<span class="tag-chip" [matTooltip]="o.group"><span class="dot" [style.background-color]="o.colour"></span>{{ o.name }}</span>}@empty {<span class="muted">–</span>}</td>
                                        }
                                    </tr>
                                    } @empty {
                                    <tr><td colspan="9" class="none">No actions{{ modelFilter ? ' in ' + modelFilter : '' }}.</td></tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                        @if (actionsOf(t).length > 25 && !allActions) {
                        <button mat-button class="more" (click)="allActions = true"><mat-icon>expand_more</mat-icon>Show all {{ actionsOf(t).length }}</button>
                        }
                    </section>
                    } @else if (g.total) {
                    <p class="pick">Pick a tag, or the untagged work, to see its actions.</p>
                    }
                </section>
                }
            </div>
            }
        </div>
        }
    `,
    styles: [`
        .status { display: flex; align-items: center; gap: 10px; margin: 16px 0; color: var(--tg-muted); }
        .error { color: #b42318; }
        :host { display: block; --tg-ink: var(--ink, #1E2233); --tg-muted: var(--slate, #5b6172); --tg-grey: var(--grey, #9aa0b0); --tg-line: var(--line, #e7e9f0);
            --tg-bg: var(--bg, #f4f5f9); --tg-surface: var(--paper, #fff); --tg-teal: var(--teal, #00a9a5); --tg-teal-soft: var(--teal-soft, #e0f5f4); --tg-teal-dark: #00706d; }
        .tags { display: flex; flex-direction: column; gap: 16px; padding: 0 0 24px; color: var(--tg-ink); max-width: 1400px; container-type: inline-size; container-name: tags; }
        .intro { margin: 0; color: var(--tg-muted); font-size: 13px; line-height: 1.5; max-width: 90ch; }
        h3 { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
        h4 { margin: 0; font-size: 14px; font-weight: 600; }
        .note { margin: 0 0 10px; color: var(--tg-muted); font-size: 12.5px; line-height: 1.45; max-width: 90ch; }
        .none, .pick { margin: 10px 0 0; color: var(--tg-muted); font-size: 13px; }
        .muted { color: var(--tg-grey); }
        .panel { min-width: 0; padding: 14px 16px 12px; border: 1px solid var(--tg-line); border-radius: 10px; background: var(--tg-surface); }
        .more { margin-top: 6px; }
        .more mat-icon { margin-right: 2px; }

        /* nothing to show yet */
        .empty { display: flex; gap: 14px; align-items: flex-start; }
        .empty mat-icon { flex: none; width: 32px; height: 32px; font-size: 32px; color: var(--tg-grey); }
        .empty strong { display: block; font-size: 14px; }
        .empty p { margin: 4px 0 0; color: var(--tg-muted); font-size: 13px; line-height: 1.5; max-width: 70ch; }

        /* the headline figures */
        .tiles { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; }
        @container tags (max-width: 760px) { .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        .tile { display: flex; flex-direction: column; gap: 2px; min-width: 0; padding: 12px 14px; border: 1px solid var(--tg-line); border-radius: 10px; background: var(--tg-surface); }
        .tile .label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--tg-muted); }
        .tile .label mat-icon { width: 16px; height: 16px; font-size: 16px; color: var(--tg-grey); }
        /* a project's figure against the current state's */
        .chg { align-self: flex-start; margin-top: 4px; font-size: 11px; font-weight: 700; white-space: nowrap; padding: 1px 6px; border-radius: 6px; }
        td .chg { display: block; width: fit-content; margin: 2px 0 0 auto; }
        .chg.good { background: #e4f6ec; color: #1e7b45; }
        .chg.bad { background: #fbe4e2; color: #b42318; }
        .chg.flat { background: #eef0f6; color: #6b7280; }
        .tile strong { font-size: 22px; font-weight: 600; line-height: 1.25; font-variant-numeric: tabular-nums; }
        .tile .of { font-size: 14px; font-weight: 500; color: var(--tg-muted); }
        .tile .sub { font-size: 12px; color: var(--tg-muted); line-height: 1.35; }

        /* groups on the left, the chosen group on the right; one under the other when there isn't room */
        .body { display: grid; grid-template-columns: minmax(220px, 280px) minmax(0, 1fr); gap: 16px; align-items: start; }
        @container tags (max-width: 820px) { .body { grid-template-columns: minmax(0, 1fr); } }
        .groups ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
        .group { display: flex; flex-direction: column; gap: 4px; width: 100%; padding: 8px 10px; border: 1px solid transparent; border-radius: 8px; background: transparent; font: inherit; color: inherit; text-align: left; cursor: pointer; }
        .group:hover { background: var(--tg-bg); }
        .group.active { border-color: #9fd8d5; background: var(--tg-teal-soft); }
        .group-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
        .group-head strong { font-size: 13.5px; font-weight: 600; overflow-wrap: anywhere; }
        .count { flex: none; padding: 0 7px; border-radius: 8px; background: var(--tg-bg); color: var(--tg-muted); font-size: 11.5px; font-weight: 600; }
        .group.active .count { background: #fff; }
        .coverage { display: grid; grid-template-columns: minmax(0, 1fr) 40px; align-items: center; gap: 8px; }
        .track { position: relative; height: 6px; border-radius: 3px; background: var(--tg-bg); overflow: hidden; }
        .group.active .track { background: #fff; }
        .track .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 3px; background: var(--tg-teal); }
        .coverage .value { text-align: right; font-size: 12px; font-variant-numeric: tabular-nums; color: var(--tg-muted); }
        .meta { font-size: 11.5px; color: var(--tg-muted); }
        @container tags (max-width: 820px) {
            .groups ul { flex-direction: row; flex-wrap: wrap; }
            .groups li { flex: 1 1 200px; min-width: 0; }
        }

        /* the chosen group: its split as one bar, then its tags */
        .detail-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px 16px; flex-wrap: wrap; }
        .detail-head .note { margin-bottom: 6px; }
        .measure { flex: none; }
        .measure ::ng-deep .mat-button-toggle-label-content { line-height: 30px; font-size: 12.5px; padding: 0 10px; }
        .split { display: flex; gap: 2px; height: 18px; margin: 10px 0 14px; border-radius: 6px; overflow: hidden; background: var(--tg-bg); }
        .segment { flex-basis: 0; min-width: 3px; cursor: pointer; opacity: .92; transition: opacity .15s; }
        .segment:hover, .segment.selected { opacity: 1; box-shadow: inset 0 0 0 2px rgba(0,0,0,.25); }
        .segment.untagged, .mini .fill.untagged, .swatch.untagged { background-color: #d6d9e1; background-image: repeating-linear-gradient(135deg, rgba(255,255,255,.7) 0 3px, transparent 3px 6px); }

        /* tables: the quiet style the other reports use */
        .scroll { overflow-x: auto; max-width: 100%; }
        table { border-collapse: collapse; font-size: 13px; }
        .list { width: 100%; min-width: 520px; }
        .list th { height: 36px; padding: 0 10px; background: var(--tg-bg); border-bottom: 1px solid var(--tg-line); color: var(--tg-muted);
            font-size: 11.5px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase; text-align: left; white-space: nowrap; }
        .list th.num { text-align: right; }
        .list td { height: 40px; padding: 4px 10px; border-bottom: 1px solid var(--tg-line); vertical-align: middle; }
        .list tbody tr { cursor: pointer; }
        .list.actions tbody tr { cursor: default; }
        .list tbody tr:hover td { background: #f8f9fb; }
        .list tbody tr.selected td { background: var(--tg-teal-soft); }
        .list tbody tr.quiet td { color: var(--tg-grey); }
        .list tbody tr.untagged td { border-top: 1px solid #cfd3dd; }
        .list tfoot td { height: 38px; padding: 0 10px; background: var(--tg-bg); border-top: 1px solid #cfd3dd; font-weight: 600; font-variant-numeric: tabular-nums; }
        .list td.first { font-weight: 500; white-space: nowrap; }
        .tag-name { display: block; }
        .share { display: flex; align-items: center; gap: 8px; margin-top: 4px; font-weight: 400; }
        .share .mini { width: 90px; }
        .share .mini-value { margin: 0; min-width: 32px; }
        /* the lesser columns go when the panel is narrow */
        @container tags (max-width: 1200px) { .optional { display: none; } }
        .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .swatch { display: inline-block; width: 12px; height: 12px; margin-right: 8px; border-radius: 3px; vertical-align: -1px; }
        .tag-note { margin-left: 8px; font-size: 12px; font-weight: 400; color: var(--tg-grey); }
        .mini { display: inline-block; position: relative; width: 80px; height: 8px; background: var(--tg-bg); border-radius: 4px; vertical-align: middle; overflow: hidden; }
        .mini .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 4px; }
        .mini-value { margin-left: 8px; font-variant-numeric: tabular-nums; font-size: 12.5px; color: var(--tg-muted); }

        /* the chosen tag's actions */
        .drill { margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--tg-line); }
        .drill-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
        .drill-head h4 { display: flex; align-items: center; gap: 8px; }
        .drill-head .swatch { margin: 0; }
        .models { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0 10px; }
        .chip { padding: 2px 10px; border: 1px solid var(--tg-line); border-radius: 12px; background: var(--tg-bg); font: inherit; font-size: 12px; color: var(--tg-ink); cursor: pointer; }
        .chip:hover { border-color: #cfd3dd; }
        .chip.active { border-color: #9fd8d5; background: var(--tg-teal-soft); color: var(--tg-teal-dark); font-weight: 600; }
        .list.actions { min-width: 860px; }
        .link { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: none; font: inherit; font-weight: 500; color: var(--tg-teal-dark); cursor: pointer; text-align: left; }
        .link mat-icon { width: 14px; height: 14px; font-size: 14px; opacity: 0; transition: opacity .15s; }
        .link:hover { text-decoration: underline; } .link:hover mat-icon { opacity: 1; }
        .list td.model { white-space: nowrap; }
        .roles { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--tg-muted); }
        .how { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 12px; font-weight: 500; white-space: nowrap; background: var(--tg-bg); color: var(--tg-muted); }
        .how-1 { background: var(--tg-teal-soft); color: var(--tg-teal-dark); }
        .how-2, .how-3 { background: #fdf3dd; color: #8a5a00; }
        .how-4 { background: #eef1f7; color: #3b4a6b; }
        .tile.warn { border-left: 4px solid #fab219; }
        .exception { color: #8a5a00; font-weight: 600; }
        .exception-chip { display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 10px; background: #fdf3dd; color: #8a5a00; font-size: 11.5px; font-weight: 600; vertical-align: 1px; }
        .other { white-space: nowrap; }
        .tag-chip { display: inline-flex; align-items: center; gap: 5px; margin: 1px 4px 1px 0; padding: 1px 8px; border: 1px solid var(--tg-line); border-radius: 6px; background: var(--tg-bg); font-size: 12px; }
        .dot { width: 8px; height: 8px; border-radius: 50%; }

        /* very narrow (the insights panel beside a model) */
        @container tags (max-width: 480px) {
            .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .panel { padding: 12px; }
            .tile strong { font-size: 19px; }
        }
    `],
})
export class TagsAnalysisComponent implements OnChanges {
    @Input() linqset?: Linqset;
    @Input() organisationLinqset?: Linqset;          // the organisation's nodes, for the names of roles and systems
    @Input() trigger: any;                           // the insights were refreshed

    loading = false;
    error = '';
    private request = 0;
    readonly measures = TAG_MEASURES;
    analysis?: TagsAnalysis;
    baseline?: TagsAnalysis;                         // the current state's, when a project is looked at
    group?: TagGroupSummary;
    tag?: TagTotals;
    groupKey = '';
    tagKey: string | undefined;
    measure: TagMeasure = 'hours';
    modelFilter = '';
    allActions = false;
    tagCount = 0;
    private currency: Currency = getCurrency(DEFAULT_CURRENCY);

    constructor(private formatter: FormattingService, private router: Router, private insightsService: InsightsService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['linqset'] || changes['trigger']) void this.load();
    }

    // the actions in what's being looked at, with their figures, from the analysis graph the other reports use; a scope
    // keeps just the actions in its models
    private async load() {
        const linqset = this.linqset;
        if (!linqset) { this.analysis = undefined; return; }
        const organisation = linqset.organisation;
        const scope = scopeForLinqset(linqset);
        const scoped = !isWholeOrganisation(scope);
        const modelKeys = new Set(scoped ? modelsInScope(scope, organisation).map(m => m._key) : []);
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            // a project is compared with the current state, over the same models
            const [graph, currentGraph] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(organisation, filterForScope(scope)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
                comparesWithCurrentState(scope) ? (this.insightsService.loadAnalysisGraphForFilter(organisation, filterForScope({ ...scope, workspaceKey: CURRENT_STATE_PROJECT_KEY })) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>)
                    .catch(err => { console.error('Unable to load the current state to compare with', err); return undefined; }) : undefined,
            ]);
            if (request !== this.request) return;
            this.baseline = currentGraph ? this.analyse(scoped ? currentGraph.nodes.filter(n => modelKeys.has(n.modelKey)) : currentGraph.nodes) : undefined;
            this.build(scoped ? graph.nodes.filter(n => modelKeys.has(n.modelKey)) : graph.nodes);
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to work out the tags', err);
            this.error = "The tags couldn't be worked out. Please try again.";
        } finally {
            if (request === this.request) this.loading = false;
        }
    }

    private analyse(nodes: AnalysisNode[]) {
        const linqset = this.linqset!;
        const organisation = linqset.organisation;
        return buildTagsAnalysis({
            nodes,
            tagGroups: organisation.tagGroups || [],
            modelNames: Object.fromEntries((organisation.models || []).map(m => [m._key, m.name])),
            nameOf: (key: string) => (linqset.getNode(key) || this.organisationLinqset?.getNode(key))?.name || '',
        });
    }

    private build(nodes: AnalysisNode[]) {
        this.currency = getCurrency(this.linqset!.organisation.currencyCode || DEFAULT_CURRENCY);
        this.analysis = this.analyse(nodes);
        this.tagCount = this.analysis.groups.reduce((sum, g) => sum + g.tags.length, 0);
        // keep the group and tag being looked at across a refresh, else start with the most used group
        const group = this.analysis.groups.find(g => g.key === this.groupKey) || this.analysis.groups[0];
        this.selectGroup(group, true);
    }

    // a headline figure against the current state's, when a project is looked at
    vs(figure: keyof ReturnType<typeof tagsFigures>): Change | undefined {
        if (!this.baseline || !this.analysis) return undefined;
        return changeFrom(tagsFigures(this.baseline)[figure], tagsFigures(this.analysis)[figure], this.changeFormat);
    }

    // a tag's hours or cost against the same tag's in the current state
    tagChange(group: TagGroupSummary, tag: TagTotals, field: 'hours' | 'cost'): Change | undefined {
        if (!this.baseline || !tag.actions) return undefined;
        const g = this.baseline.groups.find(b => b.key === group.key);
        const before = g && (tag.untagged ? g.untagged : g.tags.find(t => t.key === tag.key));
        const unit = field === 'hours' ? 'hours' : 'money';
        return changeFrom(before ? measure(before[field], 'none', unit) : undefined, measure(tag[field], 'none', unit), this.changeFormat);
    }

    private get changeFormat() {
        return changeFormat(v => this.money(v), v => this.count(v));
    }

    selectGroup(group: TagGroupSummary | undefined, keepTag = false) {
        this.group = group;
        this.groupKey = group?.key || '';
        const tag = keepTag && group ? this.findTag(group, this.tagKey) : undefined;
        this.selectTag(group, tag);
    }

    selectTag(group: TagGroupSummary | undefined, tag: TagTotals | undefined) {
        this.tag = tag && group ? this.findTag(group, tag.key) : undefined;
        this.tagKey = this.tag?.key;
        this.modelFilter = '';
        this.allActions = false;
    }

    private findTag(group: TagGroupSummary, key: string | undefined) {
        if (key === undefined) return undefined;
        return key === UNTAGGED_KEY ? group.untagged : group.tags.find(t => t.key === key);
    }

    setMeasure(measure: TagMeasure) {
        this.measure = measure;
    }

    get measureLabel() {
        return this.measures.find(m => m.key === this.measure)?.label || '';
    }

    get unit() {
        return this.measures.find(m => m.key === this.measure)?.unit || '';
    }

    // the group's tags with the untagged work last, in the order of the measure
    rows(g: TagGroupSummary): TagTotals[] {
        return [...[...g.tags].sort((a, b) => measureOf(b, this.measure) - measureOf(a, this.measure) || b.actions - a.actions), g.untagged];
    }

    // the split bar: the tags with work in them, then the untagged work
    segments(g: TagGroupSummary): TagTotals[] {
        return this.rows(g).filter(t => measureOf(t, this.measure) > 0);
    }

    measureOf(t: TagTotals) {
        return measureOf(t, this.measure);
    }

    // a tag's (or the untagged work's) share of all the group's work, tagged and not, for the measure
    shareOfAll(g: TagGroupSummary, t: TagTotals) {
        const total = measureOf({ actions: g.tagged, hours: g.hours, cost: g.cost, carbon: g.carbon, items: g.items }, this.measure) + measureOf(g.untagged, this.measure);
        return total ? measureOf(t, this.measure) / total : 0;
    }

    splitLabel(g: TagGroupSummary) {
        return this.segments(g).map(t => t.name + ' ' + Math.round(this.shareOfAll(g, t) * 100) + '%').join(', ');
    }

    // a tag's share of the group's tagged work for the measure
    share(g: TagGroupSummary, t: TagTotals) {
        const total = measureOf({ actions: g.tagged, hours: g.hours, cost: g.cost, carbon: g.carbon, items: g.items }, this.measure);
        return total ? measureOf(t, this.measure) / total : 0;
    }

    // the actions carrying the tag, in the model chosen, most of the measure first
    actionsOf(t: TagTotals): TaggedAction[] {
        const keys = new Set(t.actionKeys);
        const all = (this.analysis?.actions || []).filter(a => keys.has(a.key) && (!this.modelFilter || a.model === this.modelFilter))
            .sort((a, b) => (this.measure === 'actions' ? b.hours - a.hours : measureOf(b as any, this.measure) - measureOf(a as any, this.measure)));
        return this.allActions ? all : all.slice(0, 25);
    }

    otherGroups(g: TagGroupSummary) {
        return (this.analysis?.groups || []).filter(o => o.key !== g.key);
    }

    // the tags an action carries in the other groups
    otherTags(a: TaggedAction, g: TagGroupSummary) {
        return this.otherGroups(g).map(o => ({ group: o.name, tag: o.tags.find(t => t.key === a.tagByGroup[o.key]) }))
            .filter(x => x.tag).map(x => ({ key: x.tag!.key, name: x.tag!.name, colour: x.tag!.colour, group: x.group }));
    }

    howDone(type: ACTION_TYPE) {
        return HOW_DONE[type] || HOW_DONE[ACTION_TYPE.NONE];
    }

    // a tag's exception work: its cost (or hours, where it isn't priced) and how many of its actions it is
    exceptionOf(t: TagTotals) {
        if (!t.exceptionActions) return '–';
        const amount = t.exceptionCost ? this.money(t.exceptionCost) : `${this.hours(t.exceptionHours)} h`;
        return `${amount} (${t.exceptionActions} of ${t.actions})`;
    }

    // the action in its model, selected
    openAction(a: TaggedAction) {
        const linqset = this.linqset;
        if (!linqset) return;
        void this.router.navigate([SKETCH_URL(linqset.organisation._key, a.modelKey, MODEL_TYPE.PROCESS, linqset.workspaceKey)], { queryParams: { nodeKey: a.key } });
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, this.currency);
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    count(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    format(value: number) {
        return this.measure === 'cost' ? this.money(value) : this.count(value);
    }
}
